import { useState, useEffect, useRef } from "react";
import { Brain, Sparkles, Cpu, Bot, Zap, MessageSquare } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import StructuredData from "./StructuredData";

const IAComparacion = () => {
  <StructuredData type="iacomparacion" />;
  const { language, translations } = useLanguage();
  const comparacion = translations[language].comparacion;
  const [visibleCards, setVisibleCards] = useState([]);
  const [typingIndex, setTypingIndex] = useState({});
  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  const typeWriter = (modelId, text, index = 0) => {
    if (index < text.length) {
      setTypingIndex((prev) => ({
        ...prev,
        [modelId]: index + 1,
      }));

      setTimeout(() => {
        typeWriter(modelId, text, index + 1);
      }, 15);
    }
  };

  const aiModels = [
    {
      id: comparacion.modelosIA[0].id,
      name: comparacion.modelosIA[0].name,
      color: "from-green-500 to-emerald-600",
      icon: <MessageSquare size={24} />,
      response: comparacion.modelosIA[0].response,
    },
    {
      id: comparacion.modelosIA[1].id,
      name: comparacion.modelosIA[1].name,
      color: "from-orange-500 to-amber-600",
      icon: <Sparkles size={24} />,
      response: comparacion.modelosIA[1].response,
    },
    {
      id: comparacion.modelosIA[2].id,
      name: comparacion.modelosIA[2].name,
      color: "from-blue-500 to-indigo-600",
      icon: <Brain size={24} />,
      response: comparacion.modelosIA[2].response,
    },
    {
      id: comparacion.modelosIA[3].id,
      name: comparacion.modelosIA[3].name,
      color: "from-purple-500 to-pink-600",
      icon: <Zap size={24} />,
      response: comparacion.modelosIA[3].response,
    },
    {
      id: comparacion.modelosIA[4].id,
      name: comparacion.modelosIA[4].name,
      color: "from-cyan-500 to-blue-600",
      icon: <Cpu size={24} />,
      response: comparacion.modelosIA[4].response,
    },
    {
      id: comparacion.modelosIA[5].id,
      name: comparacion.modelosIA[5].name,
      color: "from-violet-500 to-purple-600",
      icon: <Bot size={24} />,
      response: comparacion.modelosIA[5].response,
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            aiModels.forEach((model, index) => {
              setTimeout(() => {
                setVisibleCards((prev) => [...prev, model.id]);
                setTimeout(() => {
                  typeWriter(model.id, model.response);
                }, 300);
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="sobre-ia"
      ref={sectionRef}
      className="py-20 bg-white dark:bg-gradient-to-b dark:from-gray-800 dark:to-gray-900"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#12A9B0]/10 rounded-full mb-4">
            <Sparkles size={20} className="text-[#12A9B0]" />
            <span className="text-sm font-medium text-[#12A9B0]">
              {comparacion.titulos.comparacionIAs}
            </span>
          </div>

          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            {comparacion.titulos.preguntaMultiplePerspectivas}
          </h2>

          <p className="text-xl max-w-3xl mx-auto mb-8 text-gray-600 dark:text-gray-400">
            {comparacion.textos.descripcion}
          </p>

          <div className="inline-block">
            <div className="bg-gradient-to-r from-[#12A9B0] to-purple-600 p-1 rounded-2xl">
              <div className="px-8 py-4 rounded-2xl bg-white dark:bg-gray-900">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  "{comparacion.textos.pregunta}"
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 overflow-x-auto pb-4 snap-x">
          <div className="flex md:contents">
            {aiModels.map((model) => {
              const isTyping = typingIndex[model.id] < model.response.length;
              const hasStarted = typingIndex[model.id] > 0;
              const isComplete = typingIndex[model.id] >= model.response.length;

              return (
                <div
                  key={model.id}
                  className={`w-80 flex-shrink-0 md:w-auto md:flex-shrink mr-6 md:mr-0 relative transform transition-all duration-700 snap-start ${
                    visibleCards.includes(model.id)
                      ? "translate-y-0 opacity-100"
                      : "translate-y-10 opacity-0"
                  }`}
                >
                  <div className="h-full rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden group bg-white dark:bg-gray-800">
                    <div
                      className={`h-2 bg-gradient-to-r ${model.color}`}
                    ></div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className={`p-2 rounded-lg bg-gradient-to-r ${model.color} text-white`}
                        >
                          {model.icon}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                            {model.name}
                          </h3>
                          <div className="flex items-center gap-1">
                            {!hasStarted ? (
                              <>
                                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                <span className="text-xs text-gray-500 dark:text-gray-500">
                                  {comparacion.estados.esperando}
                                </span>
                              </>
                            ) : isTyping ? (
                              <>
                                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {comparacion.estados.escribiendo}
                                </span>
                              </>
                            ) : isComplete ? (
                              <>
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-xs text-green-600 dark:text-green-400">
                                  {comparacion.estados.completado}
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="min-h-[150px]">
                        <p className="text-[1.1em] leading-relaxed text-gray-700 dark:text-gray-300">
                          {model.response.substring(
                            0,
                            typingIndex[model.id] || 0
                          )}
                          {isTyping && hasStarted && (
                            <span className="inline-block w-0.5 h-4 bg-gray-600 dark:bg-gray-400 animate-blink ml-1"></span>
                          )}
                        </p>
                      </div>

                      {isComplete && (
                        <div className="mt-4 flex items-center gap-2 text-green-500 animate-fadeIn">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707-9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-xs font-medium">
                            {comparacion.estados.respuestaGenerada}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="text-center mt-16">
          <p
            className="mb-6 text-gray-600 dark:text-gray-400"
            dangerouslySetInnerHTML={{
              __html: comparacion.textos.enHeuristiko,
            }}
          />
          <button
            onClick={() =>
              document
                .getElementById("contacto")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#12A9B0] to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
          >
            {comparacion.botones.descubrirMetodo}
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default IAComparacion;
